/**
 * 📑📚 Paradox Diaries Codex — Multi-book fragment lore hub (Feb 2026)
 *
 * Two-layer reveal per fragment:
 *   1. Terminal boot header (green-on-black, CRT/matrix)
 *   2. Ornate handwritten body with author's stylized signature
 *
 * Backed by:
 *   GET  /api/paradox-diaries/books
 *   GET  /api/paradox-diaries/fragments?book_id=…
 *   GET  /api/paradox-diaries/fragments/:id
 *   POST /api/admin/paradox-diaries/fragments   (owner)
 *   DELETE /api/admin/paradox-diaries/fragments/:id (owner)
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT = {
  emerald: {
    text: "text-emerald-300", ring: "ring-emerald-500/50", border: "border-emerald-500/40",
    grad: "from-emerald-600/70 to-emerald-900/70", chip: "bg-emerald-950/70 text-emerald-200 border-emerald-600/50",
    dot: "bg-emerald-400", scan: "text-emerald-400",
  },
  amber: {
    text: "text-amber-300", ring: "ring-amber-500/50", border: "border-amber-500/40",
    grad: "from-amber-600/70 to-amber-900/70", chip: "bg-amber-950/70 text-amber-200 border-amber-600/50",
    dot: "bg-amber-400", scan: "text-amber-400",
  },
};

export default function ParadoxDiariesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [activeBookId, setActiveBookId] = useState(null);
  const [fragments, setFragments] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [activeFragmentId, setActiveFragmentId] = useState(searchParams.get("fragment") || null);
  const [loading, setLoading] = useState(true);
  const [adminOpen, setAdminOpen] = useState(false);

  // ── Load books once ──
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/paradox-diaries/books`);
        const list = r.data?.books || [];
        setBooks(list);
        if (list[0] && !activeBookId) setActiveBookId(list[0].book_id);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load books.");
      }
    })();
  }, []);

  // ── Load fragments for active book ──
  const refreshFragments = async (bookId) => {
    if (!bookId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/paradox-diaries/fragments?book_id=${bookId}`);
      setFragments(r.data?.fragments || []);
      setIsOwner(Boolean(r.data?.is_owner));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load fragments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshFragments(activeBookId); }, [activeBookId]);

  // ── If ?fragment= is set, keep the URL param synced ──
  useEffect(() => {
    if (activeFragmentId) setSearchParams({ fragment: activeFragmentId });
    else setSearchParams({});
  }, [activeFragmentId]);

  // ── Look up which book the active fragment belongs to (auto-switch tab) ──
  useEffect(() => {
    if (!activeFragmentId || !fragments.length) return;
    const hit = fragments.find((f) => f.fragment_id === activeFragmentId);
    if (!hit) {
      // Might live in a different book — fetch it directly
      (async () => {
        try {
          const r = await axios.get(`${API}/paradox-diaries/fragments/${activeFragmentId}`);
          const bid = r.data?.book_id;
          if (bid && bid !== activeBookId) setActiveBookId(bid);
        } catch (_) { /* silent */ }
      })();
    }
  }, [activeFragmentId, fragments, activeBookId]);

  const activeBook = useMemo(
    () => books.find((b) => b.book_id === activeBookId) || null,
    [books, activeBookId],
  );
  const activeAccent = ACCENT[activeBook?.accent] || ACCENT.emerald;

  const activeFragment = useMemo(
    () => fragments.find((f) => f.fragment_id === activeFragmentId) || null,
    [fragments, activeFragmentId],
  );

  return (
    <div className="min-h-screen flex flex-col bg-black text-white" data-testid="paradox-diaries-page">
      <Header />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 pt-20 sm:pt-24 pb-6 space-y-6">
        {/* HERO — Terminal boot log framing */}
        <section className="relative overflow-hidden rounded-2xl border border-neutral-800 bg-black p-6 font-mono text-xs sm:text-sm" data-testid="paradox-hero">
          <div className="absolute inset-0 pointer-events-none paradox-scanlines opacity-30" />
          <div className="relative">
            <div className={`${activeAccent.scan} animate-pulse`}>&gt; boot_sequence init...</div>
            {(activeBook?.boot_header || []).map((line, i) => (
              <div key={i} className={activeAccent.scan}>{line}</div>
            ))}
            <div className={`${activeAccent.scan} mt-1`}>&gt; runtime <span className="inline-block w-2 h-3 bg-current align-middle animate-cursor-blink" /></div>
          </div>
          <div className="relative mt-4 font-sans">
            <div className="text-3xl sm:text-5xl font-black tracking-tight text-white" style={{ fontFamily: "Anton, sans-serif" }}>
              THE PARADOX DIARIES
            </div>
            <div className={`text-sm ${activeAccent.text} mt-1 italic`}>
              Fractured Timeline Index — a story-in-a-story-within-a-story.
            </div>
          </div>
        </section>

        {/* BOOK SELECTOR */}
        <section data-testid="paradox-book-selector">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">📚 Choose a book</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {books.map((b) => {
              const acc = ACCENT[b.accent] || ACCENT.emerald;
              const active = activeBookId === b.book_id;
              return (
                <button
                  key={b.book_id}
                  onClick={() => { setActiveBookId(b.book_id); setActiveFragmentId(null); }}
                  data-testid={`paradox-book-btn-${b.book_id}`}
                  className={`text-left rounded-xl p-4 border transition ${
                    active
                      ? `bg-gradient-to-br ${acc.grad} border-transparent ring-2 ${acc.ring} shadow-lg`
                      : "bg-black/40 border-neutral-800 hover:border-neutral-600"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-4xl leading-none">{b.cover_emoji}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
                        {b.title.toUpperCase()}
                      </div>
                      <div className={`text-xs mt-1 ${active ? "text-white/90" : acc.text}`}>{b.authors}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* OWNER ADMIN */}
        {isOwner && (
          <section className="rounded-xl border border-amber-500/40 bg-black/60 p-4" data-testid="paradox-owner-admin">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-amber-300">🛠️ Owner Admin</div>
              <button
                onClick={() => setAdminOpen((v) => !v)}
                data-testid="paradox-admin-toggle"
                className="text-xs px-3 py-1 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-black tracking-wider"
              >
                {adminOpen ? "Close" : "New Fragment"}
              </button>
            </div>
            {adminOpen && (
              <OwnerFragmentForm
                books={books}
                defaultBookId={activeBookId}
                onSaved={() => { setAdminOpen(false); refreshFragments(activeBookId); }}
              />
            )}
          </section>
        )}

        {/* FRAGMENT LIST or DETAIL */}
        {activeFragment ? (
          <FragmentDetail
            fragment={activeFragment}
            book={activeBook}
            accent={activeAccent}
            isOwner={isOwner}
            onBack={() => setActiveFragmentId(null)}
            onEdit={() => setAdminOpen(true)}
            onDelete={async () => {
              if (!confirm(`Delete fragment ${activeFragment.fragment_id}?`)) return;
              try {
                await axios.delete(`${API}/admin/paradox-diaries/fragments/${activeFragment.fragment_id}`);
                toast.success("Fragment deleted.");
                setActiveFragmentId(null);
                refreshFragments(activeBookId);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Delete failed.");
              }
            }}
          />
        ) : (
          <FragmentList
            fragments={fragments}
            accent={activeAccent}
            loading={loading}
            onOpen={(fid) => setActiveFragmentId(fid)}
          />
        )}
      </main>

      <Footer />

      {/* Local styles — CRT scanlines + cursor blink */}
      <style>{`
        .paradox-scanlines {
          background-image: repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.35) 0px,
            rgba(0, 0, 0, 0.35) 1px,
            transparent 1px,
            transparent 3px
          );
        }
        @keyframes cursor-blink { 0%,49% {opacity:1} 50%,100% {opacity:0} }
        .animate-cursor-blink { animation: cursor-blink 1s steps(1) infinite; }
      `}</style>
    </div>
  );
}

/* ─────────────────────── Fragment List ─────────────────────── */
function FragmentList({ fragments, accent, loading, onOpen }) {
  if (loading) {
    return <div className="text-center text-neutral-500 italic py-10">Loading fragments…</div>;
  }
  if (!fragments.length) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-800 p-10 text-center text-neutral-500 italic" data-testid="paradox-empty">
        No fragments recorded in this book yet.
      </div>
    );
  }
  return (
    <section data-testid="paradox-fragment-list">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
        📑 {fragments.length} fragment{fragments.length === 1 ? "" : "s"}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fragments.map((f) => {
          const locked = f.locked;
          return (
            <button
              key={f.fragment_id}
              onClick={() => onOpen(f.fragment_id)}
              data-testid={`paradox-fragment-row-${f.fragment_id}`}
              className={`text-left rounded-xl p-4 border transition ${
                locked
                  ? "border-neutral-900 bg-black/60 hover:border-neutral-700"
                  : `border-neutral-800 bg-neutral-950 hover:border-transparent hover:ring-2 ${accent.ring}`
              }`}
            >
              <div className="flex items-baseline gap-2">
                <span className={`font-mono text-sm ${accent.text}`}>#{String(f.number || 0).padStart(3, "0")}</span>
                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${accent.chip}`}>
                  {f.binding_kind}
                </span>
                {locked && <span className="text-[9px] uppercase tracking-widest text-neutral-600 ml-auto">🔒 encrypted</span>}
              </div>
              <div className={`mt-2 font-black text-white ${locked ? "opacity-40" : ""}`} style={{ fontFamily: "Anton, sans-serif" }}>
                {locked ? "[FRAGMENT ENCRYPTED]" : f.title || "(untitled)"}
              </div>
              {locked && (
                <div className="text-[10px] text-neutral-500 italic mt-1">
                  Unlock by finding the bound artifact.
                </div>
              )}
              {!locked && f.tags?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {f.tags.slice(0, 4).map((t) => (
                    <span key={t} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-neutral-900 text-neutral-400 border border-neutral-800">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────── Fragment Detail ─────────────────────── */
function FragmentDetail({ fragment, book, accent, isOwner, onBack, onEdit, onDelete }) {
  if (fragment.locked) {
    return (
      <div className="rounded-xl border border-neutral-800 bg-black/60 p-10 text-center" data-testid="paradox-fragment-locked">
        <div className="text-5xl mb-3">🔒</div>
        <div className="text-xl font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
          FRAGMENT #{String(fragment.number || 0).padStart(3, "0")} — ENCRYPTED
        </div>
        <div className="text-xs text-neutral-500 italic mt-2">
          Discover the bound artifact to reveal this fragment.
        </div>
        <button onClick={onBack} className="mt-4 text-xs text-neutral-400 hover:text-white">
          ← Back to list
        </button>
      </div>
    );
  }
  return (
    <article className="space-y-4" data-testid="paradox-fragment-detail">
      <div className="flex items-center justify-between">
        <button onClick={onBack} data-testid="paradox-fragment-back-btn" className="text-xs text-neutral-400 hover:text-white transition">
          ← Back to list
        </button>
        {isOwner && (
          <div className="flex gap-2">
            <button onClick={onEdit} data-testid="paradox-fragment-edit-btn" className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-black">
              Edit
            </button>
            <button onClick={onDelete} data-testid="paradox-fragment-delete-btn" className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-red-600 hover:bg-red-500 text-white font-black">
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Layer 1 — Terminal boot header */}
      <div className="relative overflow-hidden rounded-xl border border-neutral-900 bg-black p-4 font-mono text-[11px]">
        <div className="absolute inset-0 pointer-events-none paradox-scanlines opacity-30" />
        <div className="relative space-y-0.5">
          {(fragment.boot_header || []).map((line, i) => (
            <div key={i} className={accent.scan}>{line}</div>
          ))}
          <div className={`${accent.scan} pt-2`}>
            &gt; frag_id: <span className="text-white">{fragment.fragment_id}</span>
          </div>
          <div className={accent.scan}>
            &gt; number: <span className="text-white">#{String(fragment.number || 0).padStart(3, "0")}</span> · binding: <span className="text-white">{fragment.binding_kind}{fragment.binding_target ? ` → ${fragment.binding_target}` : ""}</span>
          </div>
          <div className={accent.scan}>
            &gt; decrypt(payload)... <span className="text-white">OK</span>
          </div>
        </div>
      </div>

      {/* Layer 2 — Handwritten body */}
      <div className={`relative rounded-2xl border ${accent.border} bg-gradient-to-br from-neutral-950 via-black to-neutral-900 p-6 sm:p-8 shadow-2xl`} data-testid="paradox-fragment-body">
        <div className="absolute top-3 right-4 font-mono text-[10px] text-neutral-600">
          FRAGMENT #{String(fragment.number || 0).padStart(3, "0")}
        </div>
        <div className={`text-2xl sm:text-3xl font-black text-white mb-4`} style={{ fontFamily: "Anton, sans-serif" }}>
          {fragment.title || "(untitled fragment)"}
        </div>
        {/* 🖼️ Illustrated fragments (e.g. #143 Cosmos Weaver) render
            the illustration above the handwritten body. Non-illustrated
            fragments simply skip this block. */}
        {fragment.illustration_url && (
          <div className="mb-5 rounded-xl overflow-hidden border border-neutral-800/80 bg-black/40" data-testid="paradox-fragment-illustration">
            <img
              src={fragment.illustration_url}
              alt={fragment.illustration_alt || fragment.title || "Fragment illustration"}
              className="w-full h-auto object-contain max-h-[520px]"
              loading="lazy"
            />
          </div>
        )}
        <div
          className="text-neutral-200 leading-relaxed italic whitespace-pre-wrap"
          style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontSize: "1.06rem" }}
        >
          &ldquo;{fragment.body}&rdquo;
        </div>
        <div className={`mt-6 text-right text-lg ${accent.text}`}>
          {fragment.signature || book?.signature_default}
        </div>
        {fragment.tags?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-neutral-800/60 flex flex-wrap gap-1">
            {fragment.tags.map((t) => (
              <span key={t} className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${accent.chip}`}>
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

/* ─────────────────────── Owner Fragment Form ─────────────────────── */
function OwnerFragmentForm({ books, defaultBookId, onSaved }) {
  const [form, setForm] = useState({
    fragment_id: "",
    book_id: defaultBookId || (books[0]?.book_id ?? ""),
    number: 0,
    title: "",
    binding_kind: "standalone",
    binding_target: "",
    body: "",
    signature: "",
    tags: "",
    discovered_default: false,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.fragment_id || !form.book_id || !form.body) {
      toast.error("fragment_id, book_id, and body are required.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/paradox-diaries/fragments`, {
        ...form,
        number: Number(form.number || 0),
        binding_target: form.binding_target || null,
        tags: (form.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`Fragment saved: ${form.fragment_id}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="paradox-admin-form">
      <label className="text-xs text-neutral-400">
        Book
        <select value={form.book_id} onChange={set("book_id")} className="mt-1 w-full bg-black border border-neutral-800 rounded px-2 py-1 text-white text-sm" data-testid="paradox-admin-book">
          {books.map((b) => <option key={b.book_id} value={b.book_id}>{b.title}</option>)}
        </select>
      </label>
      <label className="text-xs text-neutral-400">
        Fragment ID
        <input value={form.fragment_id} onChange={set("fragment_id")} placeholder="e.g. echoing_pouch_042" className="mt-1 w-full bg-black border border-neutral-800 rounded px-2 py-1 text-white text-sm" data-testid="paradox-admin-fragid" />
      </label>
      <label className="text-xs text-neutral-400">
        Number (symbolic)
        <input type="number" value={form.number} onChange={set("number")} className="mt-1 w-full bg-black border border-neutral-800 rounded px-2 py-1 text-white text-sm" data-testid="paradox-admin-number" />
      </label>
      <label className="text-xs text-neutral-400">
        Title
        <input value={form.title} onChange={set("title")} className="mt-1 w-full bg-black border border-neutral-800 rounded px-2 py-1 text-white text-sm" data-testid="paradox-admin-title" />
      </label>
      <label className="text-xs text-neutral-400">
        Binding Kind
        <select value={form.binding_kind} onChange={set("binding_kind")} className="mt-1 w-full bg-black border border-neutral-800 rounded px-2 py-1 text-white text-sm" data-testid="paradox-admin-binding">
          {["item","event","location","npc","standalone"].map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </label>
      <label className="text-xs text-neutral-400">
        Binding Target (item_id / event_key / npc_id)
        <input value={form.binding_target} onChange={set("binding_target")} placeholder="e.g. aeons_cache_5k" className="mt-1 w-full bg-black border border-neutral-800 rounded px-2 py-1 text-white text-sm" data-testid="paradox-admin-target" />
      </label>
      <label className="text-xs text-neutral-400 sm:col-span-2">
        Body (verbatim fragment text)
        <textarea value={form.body} onChange={set("body")} rows={6} className="mt-1 w-full bg-black border border-neutral-800 rounded px-2 py-1 text-white text-sm font-serif italic" data-testid="paradox-admin-body" />
      </label>
      <label className="text-xs text-neutral-400 sm:col-span-2">
        Signature override (blank = book default)
        <input value={form.signature} onChange={set("signature")} className="mt-1 w-full bg-black border border-neutral-800 rounded px-2 py-1 text-white text-sm" data-testid="paradox-admin-signature" />
      </label>
      <label className="text-xs text-neutral-400 sm:col-span-2">
        Tags (comma-separated)
        <input value={form.tags} onChange={set("tags")} placeholder="timeline, summoning, extra_planar" className="mt-1 w-full bg-black border border-neutral-800 rounded px-2 py-1 text-white text-sm" data-testid="paradox-admin-tags" />
      </label>
      <div className="sm:col-span-2 flex justify-end">
        <button onClick={save} disabled={busy} data-testid="paradox-admin-save" className="px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-black text-xs uppercase tracking-widest disabled:opacity-40">
          {busy ? "Saving…" : "💾 Save Fragment"}
        </button>
      </div>
    </div>
  );
}
